import React, { useEffect, useMemo, useState } from "react";

const STATES_API = "https://insityapp.com/api/fetch-states";
const CITIES_API = "https://insityapp.com/api/fetch-cities";

const SCHOOL_META = {
    "school-of-management": { name: "Management", accent: "management journey" },
    "school-of-commerce": { name: "Commerce", accent: "commerce journey" },
    "school-of-engineering-and-technology": { name: "Engineering & Technology", accent: "engineering journey" },
    "school-of-information-and-technology": { name: "Information & Technology", accent: "technology journey" },
    "school-of-hotel-management": { name: "Hotel Management", accent: "hospitality journey" },
    "school-of-science": { name: "Science", accent: "scientific journey" },
    "school-of-arts-and-humanities": { name: "Arts & Humanities", accent: "humanities journey" },
    "school-of-law": { name: "Law", accent: "legal journey" },
    "school-of-pharmacy": { name: "Pharmacy", accent: "pharmacy journey" },
    "school-of-design": { name: "Design", accent: "creative journey" },
    "school-of-education": { name: "Education", accent: "education journey" },
    "school-of-journalism-and-mass-communication": { name: "Journalism & Mass Communication", accent: "media journey" },
    "school-of-media-and-entertainment": { name: "Media & Entertainment", accent: "creative journey" },
    "school-of-library-and-information-science": { name: "Library & Information Science", accent: "information journey" },
    "school-of-yoga-and-naturopathy": { name: "Yoga & Naturopathy", accent: "wellness journey" },
    "school-of-life-science": { name: "Life Science", accent: "life-science journey" },
    "school-of-vocational-management": { name: "Vocational Management", accent: "professional journey" },
    "school-of-health-and-allied-science": { name: "Health & Allied Science", accent: "healthcare journey" },
};

const initialForm = {
    fullName: "",
    email: "",
    mobile: "",
    state: "",
    city: "",
    programmeLevel: "",
    programmeInterest: "",
    consent: false,
};

const optionText = (item, type) => {
    if (typeof item === "string" || typeof item === "number") return String(item);
    if (!item || typeof item !== "object") return "";
    return String(
        item.name ??
        item.label ??
        item.value ??
        item[`${type}_name`] ??
        item[type] ??
        ""
    );
};

const apiOptions = (payload, type) => {
    const collection = Array.isArray(payload)
        ? payload
        : payload?.[type === "state" ? "states" : "cities"] ??
        payload?.data ??
        payload?.results ??
        payload?.items ??
        [];

    return [...new Set(
        (Array.isArray(collection) ? collection : [])
            .map((item) => optionText(item, type).trim())
            .filter(Boolean)
    )];
};

const programmeTitle = (programme) =>
    programme?.title || programme?.name || programme?.programme_name || programme?.course_name || "Programme";

const programmeLevel = (programme) => {
    if (programme?.level) return programme.level;
    const title = programmeTitle(programme).toLowerCase();
    if (/ph\.?d|doctor/.test(title)) return "Doctoral";
    if (/master|m\.?a\.?|m\.?sc|m\.?com|m\.?tech|mba|llm|m\.?pharm/.test(title)) return "Postgraduate";
    if (/bachelor|b\.?a\.?|b\.?sc|b\.?com|b\.?tech|bba|llb|b\.?pharm/.test(title)) return "Undergraduate";
    if (/diploma|certificate/.test(title)) return "Diploma / Certificate";
    return "Other";
};

const resolveSchoolId = (school) => typeof school === "string" ? school : school?.id || school?.slug || "";

const resolveSchoolProgrammes = (courses, school) => {
    const schoolId = resolveSchoolId(school);
    const schoolObject = typeof school === "object" && school !== null ? school : null;

    if (Array.isArray(schoolObject?.courses)) return schoolObject.courses;
    if (Array.isArray(schoolObject?.programmes)) return schoolObject.programmes;

    if (courses && !Array.isArray(courses) && typeof courses === "object") {
        const keyed = courses[schoolId] || courses[schoolObject?.name] || courses[schoolObject?.school];
        if (Array.isArray(keyed)) return keyed;
    }

    if (!Array.isArray(courses)) return [];
    const schoolName = String(schoolObject?.name || schoolObject?.school || SCHOOL_META[schoolId]?.name || "").toLowerCase();
    const hasSchoolMetadata = courses.some((course) =>
        course?.school_id || course?.schoolId || course?.school_slug || course?.school || course?.school_name || course?.schoolName
    );
    const matched = courses.filter((course) => {
        const ids = [course?.school_id, course?.schoolId, course?.school_slug, course?.school?.id, course?.school]
            .filter((value) => typeof value === "string")
            .map((value) => value.toLowerCase());
        const names = [course?.school_name, course?.schoolName, course?.school?.name]
            .filter(Boolean)
            .map((value) => String(value).toLowerCase());
        return ids.includes(schoolId.toLowerCase()) || (schoolName && names.includes(schoolName));
    });
    return matched.length || hasSchoolMetadata ? matched : courses;
};

const FieldLabel = ({ children }) => (
    <span className="mb-3 block text-sm font-bold uppercase tracking-[0.13em] text-[#071A2F]">
        {children}
    </span>
);

const fieldClass = "h-12 w-full border-0 border-b border-[#071A2F]/25 bg-transparent px-0 text-base text-[#071A2F] outline-none transition placeholder:text-[#65717D]/65 focus:border-[#8B1E3F] disabled:cursor-not-allowed disabled:opacity-50";

export default function ApplyNowFormSection({
    school,
    schoolDetails,
    courses,
    onSubmit,
    submitting = false,
    admissionsPhone = "+91 93731 99999",
    admissionsEmail = "admissions@isbmuniversity.edu.in",
}) {
    const schoolObject = typeof school === "object" && school !== null ? school : schoolDetails;
    const schoolId = resolveSchoolId(typeof school === "string" ? school : schoolObject);
    const meta = SCHOOL_META[schoolId] || {
        name: schoolObject?.name || schoolObject?.school || "your chosen school",
        accent: "academic journey",
    };

    const programmes = useMemo(
        () => resolveSchoolProgrammes(courses, schoolObject || schoolId),
        [courses, schoolObject, schoolId]
    );
    const levels = useMemo(
        () => [...new Set(programmes.map(programmeLevel).filter(Boolean))],
        [programmes]
    );
    const [form, setForm] = useState(initialForm);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingStates, setLoadingStates] = useState(true);
    const [loadingCities, setLoadingCities] = useState(false);
    const [locationError, setLocationError] = useState("");
    const [status, setStatus] = useState("");

    const visibleProgrammes = useMemo(
        () => form.programmeLevel
            ? programmes.filter((programme) => programmeLevel(programme) === form.programmeLevel)
            : programmes,
        [programmes, form.programmeLevel]
    );

    useEffect(() => {
        const controller = new AbortController();
        setLoadingStates(true);
        fetch(STATES_API, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error("Unable to load states.");
                return response.json();
            })
            .then((payload) => setStates(apiOptions(payload, "state")))
            .catch((error) => {
                if (error.name !== "AbortError") setLocationError("States could not be loaded. Please try again.");
            })
            .finally(() => setLoadingStates(false));
        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (!form.state) {
            setCities([]);
            return undefined;
        }
        const controller = new AbortController();
        setLoadingCities(true);
        setLocationError("");
        fetch(`${CITIES_API}?state=${encodeURIComponent(form.state)}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error("Unable to load cities.");
                return response.json();
            })
            .then((payload) => setCities(apiOptions(payload, "city")))
            .catch((error) => {
                if (error.name !== "AbortError") setLocationError("Cities could not be loaded. Please try again.");
            })
            .finally(() => setLoadingCities(false));
        return () => controller.abort();
    }, [form.state]);

    useEffect(() => {
        setForm((current) => ({ ...current, programmeLevel: "", programmeInterest: "" }));
    }, [schoolId]);

    const update = (field) => (event) => {
        const value = field === "consent" ? event.target.checked : event.target.value;
        setStatus("");
        setForm((current) => ({
            ...current,
            [field]: value,
            ...(field === "state" ? { city: "" } : {}),
            ...(field === "programmeLevel" ? { programmeInterest: "" } : {}),
        }));
    };

    const submitForm = async (event) => {
        event.preventDefault();
        if (!form.consent) {
            setStatus("Please accept the consent statement before submitting.");
            return;
        }
        const payload = { ...form, schoolId, schoolName: meta.name };
        try {
            if (onSubmit) await onSubmit(payload);
            setStatus("Thank you. Your request has been received.");
        } catch (error) {
            setStatus(error?.message || "Your request could not be submitted. Please try again.");
        }
    };

    return (
        <section className="relative isolate overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-28" aria-labelledby="apply-now-heading">
            <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                <div className="absolute -left-40 -top-44 h-[560px] w-[560px] rounded-full bg-[#8B1E3F]/25 blur-[120px]" />
                <div className="absolute inset-y-0 left-5 right-5 border-x border-[#D5AD57]/10" />
                <div className="absolute right-12 top-0 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(10rem,24vw,28rem)] leading-none text-white/[0.018]">APPLY</div>
            </div>

            <div className="mx-auto grid w-[min(1380px,calc(100%-48px))] gap-12 max-sm:w-[calc(100%-28px)] lg:grid-cols-[.9fr_1.1fr] lg:items-center lg:gap-20">
                <div className="lg:pl-10">
                    <span className="text-sm font-bold uppercase tracking-[0.17em] text-[#D5AD57]">Admissions 2026</span>
                    <h2 id="apply-now-heading" className="mt-7 max-w-2xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(48px,6vw,82px)] font-normal leading-[1.02] tracking-[-0.045em]">
                        Your {meta.name.toLowerCase()} journey
                        <em className="mt-2 block font-normal text-[#D5AD57]">begins with a conversation.</em>
                    </h2>
                    <p className="mt-7 max-w-xl text-base leading-8 text-white/65 sm:text-lg">
                        Speak with our admissions team about programmes, eligibility, scholarships and the learning experience at the School of {meta.name}.
                    </p>
                    <div className="mt-10 border-t border-white/15">
                        {[
                            ["01", "Call admissions", admissionsPhone, `tel:${admissionsPhone.replace(/\s/g, "")}`],
                            ["02", "Write to us", admissionsEmail, `mailto:${admissionsEmail}`],
                            ["03", "Experience the campus", "Plan a campus visit", "/contact-us"],
                        ].map(([number, label, value, href]) => (
                            <a key={number} href={href} className="group grid min-h-[82px] grid-cols-[48px_1fr_auto] items-center gap-4 border-b border-white/15 py-4">
                                <span className="grid h-11 w-11 place-items-center rounded-full border border-[#D5AD57]/45 text-sm font-bold text-[#D5AD57]">{number}</span>
                                <span><small className="block text-sm uppercase tracking-[0.11em] text-white/45">{label}</small><strong className="mt-1 block text-base text-white">{value}</strong></span>
                                <span className="text-[#D5AD57] transition group-hover:-translate-y-1 group-hover:translate-x-1">↗</span>
                            </a>
                        ))}
                    </div>
                    <p className="mt-6 flex items-center gap-3 text-sm text-white/45"><span className="h-2 w-2 rounded-full bg-[#D5AD57] shadow-[0_0_0_5px_rgba(213,173,87,.1)]" />Admission desk: Monday–Saturday, 9:30 AM–5:30 PM</p>
                </div>

                <form onSubmit={submitForm} className="bg-[#F7F2E8] p-7 text-[#132337] shadow-[0_32px_90px_rgba(0,0,0,.22)] sm:p-10 lg:p-12">
                    <div className="flex items-center justify-between border-b border-[#071A2F]/15 pb-7">
                        <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal text-[#071A2F] sm:text-4xl">Request information</h3>
                        <span className="text-sm font-bold tracking-[.16em] text-[#8B1E3F]">01—08</span>
                    </div>

                    <div className="mt-8 grid gap-x-7 gap-y-7 sm:grid-cols-2">
                        <label><FieldLabel>Full name</FieldLabel><input required name="fullName" autoComplete="name" value={form.fullName} onChange={update("fullName")} placeholder="Your full name" className={fieldClass} /></label>
                        <label><FieldLabel>Email address</FieldLabel><input required type="email" name="email" autoComplete="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className={fieldClass} /></label>
                        <label><FieldLabel>Mobile number</FieldLabel><div className="flex border-b border-[#071A2F]/25 focus-within:border-[#8B1E3F]"><span className="flex h-12 items-center pr-3 font-bold text-[#8B1E3F]">+91</span><input required type="tel" name="mobile" autoComplete="tel-national" inputMode="numeric" pattern="[0-9]{10}" maxLength={10} value={form.mobile} onChange={update("mobile")} placeholder="10-digit number" className="h-12 min-w-0 flex-1 bg-transparent text-base text-[#071A2F] outline-none placeholder:text-[#65717D]/65" /></div></label>
                        <label><FieldLabel>State</FieldLabel><select required name="state" value={form.state} onChange={update("state")} disabled={loadingStates} className={fieldClass}><option value="">{loadingStates ? "Loading states..." : "Select state"}</option>{states.map((state) => <option key={state} value={state}>{state}</option>)}</select></label>
                        <label><FieldLabel>City</FieldLabel><select required name="city" value={form.city} onChange={update("city")} disabled={!form.state || loadingCities} className={fieldClass}><option value="">{loadingCities ? "Loading cities..." : form.state ? "Select city" : "Select state first"}</option>{cities.map((city) => <option key={city} value={city}>{city}</option>)}</select></label>
                        <label><FieldLabel>Programme level</FieldLabel><select required name="programmeLevel" value={form.programmeLevel} onChange={update("programmeLevel")} className={fieldClass}><option value="">Select programme level</option>{levels.map((level) => <option key={level} value={level}>{level}</option>)}</select></label>
                        <label className="sm:col-span-2"><FieldLabel>Programme interest</FieldLabel><select required name="programmeInterest" value={form.programmeInterest} onChange={update("programmeInterest")} disabled={!programmes.length} className={fieldClass}><option value="">{programmes.length ? `Select a ${meta.name} programme` : "No programmes available"}</option>{visibleProgrammes.map((programme) => { const title = programmeTitle(programme); return <option key={programme?.id || programme?.slug || title} value={title}>{title}</option>; })}</select></label>
                    </div>

                    {locationError && <p className="mt-4 text-sm font-semibold text-[#8B1E3F]" role="alert">{locationError}</p>}
                    <label className="mt-7 flex items-start gap-3 text-sm leading-6 text-[#65717D]"><input required type="checkbox" checked={form.consent} onChange={update("consent")} className="mt-1 h-4 w-4 accent-[#8B1E3F]" /><span>I agree to receive admission information by phone, email or message and accept the University’s privacy policy.</span></label>
                    {status && <p className="mt-4 text-sm font-semibold text-[#8B1E3F]" role="status">{status}</p>}
                    <button type="submit" disabled={submitting} className="mt-7 flex min-h-[62px] w-full items-center justify-between bg-[#8B1E3F] pl-6 text-base font-bold text-white transition hover:bg-[#071A2F] disabled:cursor-not-allowed disabled:opacity-60"><span>{submitting ? "Submitting..." : "Request a callback"}</span><span className="mr-2 grid h-11 w-11 place-items-center bg-[#D5AD57] text-xl text-[#071A2F]">↗</span></button>
                </form>
            </div>
        </section>
    );
}

export { SCHOOL_META as applyNowSchoolMeta };

export async function handleAdmissionEnquiry(
    formData,
    endpoint = "/api/admission-enquiry"
) {
    const response = await fetch(endpoint, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        body: JSON.stringify(formData),
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(
            result?.message ||
            "Your request could not be submitted. Please try again."
        );
    }

    return result;
}